import * as Tone from 'tone';
import type { SynthTypeId } from '../config/synth-types';
import { SYNTH_TYPES, WAVEFORM_GAIN } from '../config/synth-types';
import type { Settings } from '../state/settings';
import { ensureGraph, setSourceTrim, WORKLETS_BLOCKED } from './graph';

export type Source =
  | { kind: 'poly'; node: Tone.PolySynth }
  | { kind: 'noise'; node: Tone.NoiseSynth }
  | { kind: 'pluck'; node: Tone.PluckSynth };

let source: Source | null = null;

export const currentSource = (): Source | null => source;

export function buildEnvelopeOptions(type: SynthTypeId, s: Settings): object {
  const attack = Math.max(0.001, s.attack || 0.01);
  const release = Math.max(0.01, s.release || 0.2);
  if (SYNTH_TYPES[type].envelope === 'percussive') return { attack, decay: 0.2, release };
  return { attack, decay: 0.1, sustain: 0.9, release };
}

export function buildSynthOptions(type: SynthTypeId, s: Settings): Record<string, unknown> {
  const oscillator = { type: s.waveform };
  const envelope = buildEnvelopeOptions(type, s);
  const ex = s.extraValues[type] ?? {};
  const num = (k: string, fallback: number): number => (typeof ex[k] === 'number' ? ex[k] : fallback);

  switch (type) {
    case 'synth':
    case 'monosynth':
      return { oscillator, envelope };
    case 'amsynth':
      return { oscillator, envelope, harmonicity: num('harmonicity', 1) };
    case 'fmsynth':
      return {
        oscillator,
        envelope,
        harmonicity: num('harmonicity', 1),
        modulationIndex: num('modulationIndex', 10),
      };
    case 'duosynth':
      return {
        voice0: { oscillator, envelope },
        voice1: { oscillator, envelope },
        harmonicity: num('harmonicity', 1.5),
        vibratoAmount: num('vibratoAmount', 0.5),
      };
    case 'membrane':
      return { oscillator, envelope, pitchDecay: num('pitchDecay', 0.05), octaves: num('octaves', 6) };
    case 'metal':
      return {
        envelope,
        harmonicity: num('harmonicity', 5.1),
        modulationIndex: num('modulationIndex', 32),
        resonance: num('resonance', 4000),
        octaves: 1.5,
      };
    case 'noise':
      return {
        envelope,
        noise: { type: (['white', 'pink', 'brown'] as const)[Math.round(num('noiseType', 0))] ?? 'white' },
      };
    case 'pluck':
      return { dampening: num('dampening', 4000), resonance: Math.min(num('resonance', 0.9), 0.95) };
  }
}

/**
 * Waveform loudness compensation lives on a gain node rather than in note
 * velocity. Velocity is an envelope peak that Tone expects inside 0..1; folding
 * a 2.4x sine boost and a register curve into it (the previous approach)
 * produced values up to 2.5, which simply clipped, and left no room for the
 * bass boost to act at all because everything saturated at the clamp.
 */
export function sourceTrimFor(s: Settings): number {
  // Recorded samples arrive at their own level and need no waveform trim.
  if (s.engine === 'sampler') return 1;
  const def = SYNTH_TYPES[s.synthType];
  const wave = def.hasOscillator ? (WAVEFORM_GAIN[s.waveform] ?? 1) : 1.2;
  return wave * 1.5;
}

export function disposeSource(): void {
  if (!source) return;
  try {
    source.node.disconnect();
  } catch {
    /* ignore */
  }
  try {
    source.node.dispose();
  } catch {
    /* ignore */
  }
  source = null;
}

export interface BuildResult {
  /** Set when the requested type could not be used and a fallback was built. */
  readonly fallbackFrom?: SynthTypeId;
  readonly reason?: string;
}

export function buildSynthSource(s: Settings): BuildResult {
  const g = ensureGraph();
  disposeSource();

  let type = s.synthType;
  let result: BuildResult = {};

  if (SYNTH_TYPES[type].needsWorklet && WORKLETS_BLOCKED) {
    result = { fallbackFrom: type, reason: 'worklet' };
    type = 'synth';
  }

  const def = SYNTH_TYPES[type];
  const opts = buildSynthOptions(type, s);

  try {
    if (type === 'noise') {
      source = { kind: 'noise', node: new Tone.NoiseSynth(opts) };
    } else if (type === 'pluck') {
      source = { kind: 'pluck', node: new Tone.PluckSynth(opts) };
    } else {
      // PolySynth is generic over one Monophonic voice type; this table holds
      // seven different ones, so the constructor is narrowed here.
      const Voice = def.ctor() as unknown as typeof Tone.Synth;
      const poly = new Tone.PolySynth(Voice, opts as never);
      poly.maxPolyphony = 24;
      source = { kind: 'poly', node: poly };
    }
  } catch (err) {
    console.warn('Construirea sintetizatorului a eșuat, revin la Synth:', err);
    result = { fallbackFrom: s.synthType, reason: err instanceof Error ? err.message : String(err) };
    source = { kind: 'poly', node: new Tone.PolySynth(Tone.Synth, buildSynthOptions('synth', s)) };
  }

  source.node.connect(g.sourceTrim);
  setSourceTrim(sourceTrimFor(s));
  return result;
}

/** Push changed options onto the live source without rebuilding it. */
export function updateSourceOptions(s: Settings): void {
  setSourceTrim(sourceTrimFor(s));
  if (!source) return;
  try {
    source.node.set(buildSynthOptions(s.synthType, s) as never);
  } catch (err) {
    console.warn('Actualizarea parametrilor a eșuat:', err);
  }
}
